import { Link } from 'react-router-dom';
import { Check, Circle, AlertTriangle } from 'lucide-react';
import { SETUP_MILESTONE_COUNT, getFirstIncompleteConfigureHref } from '@/config/setup-milestones';
import { cn } from '@/lib/utils';
import { Button } from './ui/button';

export type SetupMilestone = {
  id: string;
  label: string;
  complete: boolean;
  blocked?: boolean;
  current?: boolean;
  configureHref?: string | null;
};

export type SetupProgressProps = {
  milestones: SetupMilestone[];
  variant?: 'hero' | 'compact' | 'inline';
  resumeHref?: string;
  className?: string;
};

function MilestoneIcon({ milestone }: { milestone: SetupMilestone }) {
  if (milestone.complete) return <Check className="h-4 w-4 text-success" />;
  if (milestone.blocked) return <AlertTriangle className="h-4 w-4 text-warning" />;
  if (milestone.current) return <Circle className="h-4 w-4 fill-primary text-primary" />;
  return <Circle className="h-4 w-4 text-muted-foreground" />;
}

function MilestoneChip({ milestone }: { milestone: SetupMilestone }) {
  const className = cn(
    'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs transition-colors',
    milestone.complete && 'border-success/30 bg-success/5',
    milestone.current && 'border-primary/30 bg-primary/5',
    milestone.blocked && 'border-warning/30 bg-warning/5',
    !milestone.complete && milestone.configureHref && 'hover:border-primary/40 hover:bg-primary/5'
  );

  const content = (
    <>
      <MilestoneIcon milestone={milestone} />
      <span>{milestone.label}</span>
    </>
  );

  if (!milestone.complete && milestone.configureHref) {
    return (
      <Link to={milestone.configureHref} className={className}>
        {content}
      </Link>
    );
  }

  return <div className={className}>{content}</div>;
}

/** Cross-cutting setup progress — links to Configure sections, not wizard pages. */
export function SetupProgress({
  milestones,
  variant = 'hero',
  resumeHref,
  className,
}: SetupProgressProps) {
  const completed = milestones.filter((m) => m.complete).length;
  const total = milestones.length || SETUP_MILESTONE_COUNT;
  const percent = Math.round((completed / total) * 100);
  const nextHref = resumeHref ?? getFirstIncompleteConfigureHref(milestones);

  if (variant === 'compact') {
    if (percent >= 100) return null;
    return (
      <div className={cn('rounded-lg border border-border bg-muted/50 p-3', className)}>
        <div className="mb-2 flex items-center justify-between text-xs">
          <span className="font-medium">Setup</span>
          <span className="text-muted-foreground">
            {completed}/{total}
          </span>
        </div>
        <div className="mb-2 h-1.5 overflow-hidden rounded-full bg-muted">
          <div className="h-full bg-primary transition-all" style={{ width: `${percent}%` }} />
        </div>
        <Button variant="link" size="sm" className="h-auto p-0 text-xs" asChild>
          <Link to={nextHref}>Continue setup →</Link>
        </Button>
      </div>
    );
  }

  if (variant === 'inline') {
    return (
      <div className={cn('flex items-center gap-2 text-xs', className)}>
        <div className="h-1.5 w-16 overflow-hidden rounded-full bg-muted">
          <div className="h-full bg-primary" style={{ width: `${percent}%` }} />
        </div>
        <span className="text-muted-foreground">
          {completed}/{total}
        </span>
      </div>
    );
  }

  return (
    <div className={cn('rounded-lg border border-border bg-card p-6', className)}>
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Setup Progress</h2>
          <p className="text-sm text-muted-foreground">
            {completed} of {total} milestones complete — configure in Salesforce, Telephony, and Agents.
          </p>
        </div>
        <div className="text-2xl font-bold text-primary">{percent}%</div>
      </div>
      <div className="mb-4 h-2 overflow-hidden rounded-full bg-muted">
        <div className="h-full bg-primary transition-all duration-500" style={{ width: `${percent}%` }} />
      </div>
      <div className="flex flex-wrap gap-2">
        {milestones.map((m) => (
          <MilestoneChip key={m.id} milestone={m} />
        ))}
      </div>
      {percent < 100 && (
        <div className="mt-4 flex flex-wrap gap-2">
          <Button asChild>
            <Link to={nextHref}>Continue Setup</Link>
          </Button>
          <Button variant="outline" asChild>
            <Link to="/salesforce">Salesforce</Link>
          </Button>
          <Button variant="outline" asChild>
            <Link to="/telephony">Telephony</Link>
          </Button>
        </div>
      )}
    </div>
  );
}
